import React from 'react';
import { Box, FormControl, RadioGroup, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';

// Import the FormLabel and RadioOption from the same directory
// These will be used directly from AddProductForm until they are also extracted
import { FormLabel } from './AddProductForm';
import { RadioOption } from './AddProductForm';

const useStyles = makeStyles(() => ({
  formGroup: {
    marginBottom: '30px',
  },
  formLabel: {
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '18px',
    letterSpacing: '0.15px',
    color: '#464B52',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '2px 0px',
    gap: '4px',
  },
  visibilityOption: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '12px',
  },
  visibilityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px', 
    marginBottom: 0,
  },
  radioControl: {
    '& .MuiRadio-root': {
      padding: '4px',
    },
    '& .MuiSvgIcon-root': {
      borderRadius: '50%',
    },
    '& .Mui-checked': {
      '& .MuiSvgIcon-root': {
        color: '#217CBB',
      }
    }
  },
  badge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 700,
    color: '#252A2E',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    marginLeft: '12px',
    fontFamily: "'Open Sans'",
    lineHeight: '16px',
    height: '20px',
  },
  teamBadge: {
    backgroundColor: '#FBAD26',
  },
  accountBadge: {
    backgroundColor: '#FBAD26',
  },
  organizationBadge: {
    backgroundColor: '#FBAD26',
  },
  publicBadge: {
    backgroundColor: '#FBAD26',
  },
  visibilityBadge: {
    textAlign: 'center',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '12px',
    borderRadius: '2px',
    padding: '0 8px',
  },
}));

// Type definitions
export type VisibilityType = 'team' | 'account' | 'organization' | 'public';

interface VisibilityOption {
  value: VisibilityType;
  label: React.ReactNode;
  description: React.ReactNode;
  badge: React.ReactNode;
}

interface VisibilitySelectorProps {
  visibility: VisibilityType;
  onVisibilityChange: (visibility: VisibilityType) => void;
}

export const VisibilitySelector: React.FC<VisibilitySelectorProps> = ({ 
  visibility, 
  onVisibilityChange 
}) => {
  const classes = useStyles();

  // Badge renderer with improved type safety
  const getVisibilityBadge = (type: VisibilityType) => {
    // Map types to classes and labels
    const badgeConfig: Record<VisibilityType, {class: string, label: string}> = {
      'team': { class: classes.teamBadge, label: 'TEAM' },
      'account': { class: classes.accountBadge, label: 'ACCOUNT' },
      'organization': { class: classes.organizationBadge, label: 'ORGANIZATION' },
      'public': { class: classes.publicBadge, label: 'PUBLIC' }
    };
    
    const config = badgeConfig[type];
    return <span className={`${classes.badge} ${config.class} ${classes.visibilityBadge}`}>{config.label}</span>;
  };

  // Options data
  const visibilityOptions: VisibilityOption[] = [
    {
      value: 'team',
      label: <FormattedMessage defaultMessage="My Team" />,
      description: <FormattedMessage defaultMessage="This Product is visible only to my Team members" />,
      badge: getVisibilityBadge('team'),
    },
    {
      value: 'account',
      label: <FormattedMessage defaultMessage="My Account" />,
      description: <FormattedMessage defaultMessage="This Product is visible only to my Account users" />,
      badge: getVisibilityBadge('account'),
    },
    {
      value: 'organization',
      label: <FormattedMessage defaultMessage="Organization" />,
      description: <FormattedMessage defaultMessage="This Product is visible to all organization members" />,
      badge: getVisibilityBadge('organization'),
    }
  ];

  const handleVisibilityChange = (newVisibility: string) => {
    onVisibilityChange(newVisibility as VisibilityType);
  };

  return (
    <Box className={classes.formGroup}>
      <Box className={classes.formLabel}>
        <FormLabel>
          <FormattedMessage defaultMessage="Product Visibility" />
        </FormLabel>
      </Box>
      <FormControl component="fieldset" style={{ marginTop: '20px', width: '100%' }}>
        <RadioGroup
          value={visibility}
          onChange={(e) => handleVisibilityChange(e.target.value)}
        >
          {visibilityOptions.map((opt, idx) => (
            <React.Fragment key={opt.value}>
              {idx !== 0 && <Divider style={{marginBottom: '10px'}} />}
              <Box className={classes.visibilityOption}>
                <Box className={classes.visibilityRow}>
                  <RadioOption
                    checked={visibility === opt.value}
                    onChange={() => handleVisibilityChange(opt.value)}
                    value={opt.value}
                    label={opt.label}
                    description={opt.description}
                    badge={opt.badge}
                    radioClass={classes.radioControl}
                  />
                </Box>
              </Box>
            </React.Fragment>
          ))}
          <Divider style={{marginTop: '10px'}} />
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default VisibilitySelector;
